import { Injectable } from '@angular/core';
import { GlobalConfigService } from '../services/global-config.service';
import { EntityDetailsRoutes, EntityDetailsTabRoutes } from '../constants/Global/AnalyticsTracking';
import { SSOService } from '../sso/sso.service';
import { KeycloakService } from './keycloak.service';
import { UserService } from '../services/user.service';

declare var gtag: Function;

@Injectable({
  providedIn: 'root',
})
export class GoogleAnalyticsService {
  private eventQueue: Array<() => void> = [];
  private isProcessingQueue = false;
  private readonly EVENT_DELAY = 150; // 150ms delay between events
  private currentUserType: string | null = null;
  private userId: string;
  lastLocation: string;

  constructor(
    private globalConfigSvc: GlobalConfigService,
    private ssoService: SSOService,
    private keycloakService: KeycloakService,
    private userService: UserService
  ) {
    this.lastLocation = document.referrer || null;
  }

  initialize() {
    this.setupUserTypeTracking();

    try {
      const trackingCode = this.globalConfigSvc.globalConfig.googleAnalyticsTrackingCode;
      const url = 'https://www.googletagmanager.com/gtag/js?id=';
      const gTagScript = document.createElement('script');
      gTagScript.async = true;
      gTagScript.src = `${url}${trackingCode}`;
      document.head.appendChild(gTagScript);

      const dataLayerScript = document.createElement('script');
      dataLayerScript.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        window.gtag = gtag;
        gtag('js', new Date());`;

      document.head.appendChild(dataLayerScript);

      setTimeout(() => {
        this.setInitialUserPropertiesWithConfig(trackingCode);
      }, 100);
    } catch (e) {
      console.error('Error adding Google Analytics', e);
    }
  }

  public trackPageView(url: string): void {
    if (this.shouldSkipTracking(url)) {
      return;
    }

    this.queueEvent(() => {
      // Ensure user properties are up to date before tracking
      this.setUserTypeProperty();

      const userType = this.currentUserType || 'anonymous';
      const userId = this.userId || null;

      // Set user properties first
      gtag('set', 'user_properties', { user_type: userType, GSUserID: userId });
      gtag('set', { user_type: userType, GSUserID: userId });

      const page_location = window.location.href;
      // Send the page_view event with the properties and page title
      gtag('event', 'page_view', {
        page_path: url,
        page_location: window.location.href,
        page_title: document.title,
        user_type: userType,
        GSUserID: userId,
        ...(this.lastLocation ? { page_referrer: this.lastLocation } : {}),
      });

      // Update for next hop
      this.lastLocation = page_location;
    });
  }

  private shouldSkipTracking(url: string): boolean {
    const isDetailsRoute = EntityDetailsRoutes.some((route) => url.indexOf(route) !== -1);
    const isDetailsTabRoute = EntityDetailsTabRoutes.some((tabRoute) => url.indexOf(tabRoute) !== -1);

    // Skip tracking if it's a details route AND a tab route (not the main details page)
    return isDetailsRoute && isDetailsTabRoute;
  }

  private async processEventQueue(): Promise<void> {
    if (this.isProcessingQueue || this.eventQueue.length === 0) {
      return;
    }

    this.isProcessingQueue = true;

    while (this.eventQueue.length > 0) {
      const eventFunction = this.eventQueue.shift();
      if (eventFunction) {
        try {
          eventFunction();
        } catch (error) {
          console.error('Error processing GA event:', error);
        }

        // Wait before processing next event
        if (this.eventQueue.length > 0) {
          await new Promise((resolve) => setTimeout(resolve, this.EVENT_DELAY));
        }
      }
    }

    this.isProcessingQueue = false;
  }

  private queueEvent(eventFunction: () => void): void {
    this.eventQueue.push(eventFunction);
    ``;
    this.processEventQueue();
  }

  public event(action: string, eventCategory?: string, eventLabel?: string, value?: string) {
    this.queueEvent(() => {
      const userType = this.currentUserType || 'anonymous';

      gtag('set', 'user_properties', { user_type: userType, GSUserID: this.userId });
      gtag('set', { user_type: userType, GSUserID: this.userId });

      const eventData = {
        ...(eventCategory && { event_category: eventCategory }),
        ...(eventLabel && { event_label: eventLabel }),
        ...(value && { value: value }),
        user_type: userType,
        GSUserID: this.userId,
      };

      gtag('event', action, eventData);
    });
  }

  trackButtonClick(event: string, category: string, label?: string, value?: string): void {
    this.queueEvent(() => {
      const userType = this.currentUserType || 'anonymous';

      gtag('set', 'user_properties', { user_type: userType, GSUserID: this.userId });
      gtag('set', { user_type: userType, GSUserID: this.userId });

      const eventData = {
        event_category: category,
        event_label: label,
        value: value,
        user_type: userType,
        GSUserID: this.userId,
      };

      gtag('event', event, eventData);
    });
  }

  private setupUserTypeTracking(): void {
    this.setUserTypeProperty();

    this.userService.currentUser$.subscribe(() => {
      this.setUserTypeProperty();
    });

    this.ssoService.isAuthenticated$.subscribe(() => {
      this.setUserTypeProperty();
    });

    this.keycloakService.isAuthenticated$.subscribe(() => {
      this.setUserTypeProperty();
    });
  }

  private setUserTypeProperty(): void {
    const isLoggedIn = this.ssoService.isLoggedIn || this.keycloakService.isLoggedIn;
    let userType = 'anonymous';

    if (isLoggedIn) {
      this.userId = this.userService.currentUser.userUuid;
      this.setUserProperties({ GSUserID: this.userId });
      if (this.userService.currentUser?.userType) {
        userType = this.userService.currentUser.userType;
      }
    }

    if (this.currentUserType !== userType) {
      this.currentUserType = userType;
      this.setUserProperties({ user_type: userType });
    }
  }

  public updateUserType(): void {
    this.setUserTypeProperty();
  }

  public setUserProperties(properties: { [key: string]: any }): void {
    this.queueEvent(() => {
      try {
        // const trackingId = this.globalConfigSvc.globalConfig.googleAnalyticsTrackingCode;

        gtag('set', 'user_properties', properties);
        if (properties.user_type) {
          gtag('set', { user_type: properties.user_type });
        }

        if (properties.GSUserID) {
          gtag('set', { GSUserID: properties.GSUserID });
        }

        // gtag('config', trackingId, {
        //   user_properties: properties,
        //   custom_map: { user_type: 'user_type', userId: 'userId' },
        //   send_page_view: false,
        // });
      } catch (error) {
        console.error('Error setting gtag user properties:', error);
      }
    });
  }

  private setInitialUserPropertiesWithConfig(trackingId: string): void {
    const isLoggedIn = this.ssoService.isLoggedIn || this.keycloakService.isLoggedIn;
    let userType = 'anonymous';

    if (isLoggedIn) {
      this.userId = this.userService.currentUser?.userUuid;
      if (this.userService.currentUser?.userType) {
        userType = this.userService.currentUser.userType;
      }
    }

    // Set initial user properties
    gtag('set', 'user_properties', { user_type: userType, GSUserID: this.userId });
    gtag('set', { user_type: userType, GSUserID: this.userId });

    // Configure Google Analytics with additional parameters to prevent automatic tracking
    gtag('config', trackingId, {
      send_page_view: false,
      user_properties: { user_type: userType, GSUserID: this.userId },
      custom_map: { user_type: 'user_type', GSUserID: 'GSUserID' },
      // Additional parameters to suppress automatic events
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    });

    this.currentUserType = userType;
    this.setUserTypeProperty();
  }
}
